/**
 * Automatic diagnostic-context collection for bug reports (S-2 follow-on).
 * Collected at submit time in BugReportDialog and sent as the `context`
 * field of POST /support/tickets; the API whitelists/clamps it server-side
 * (apps/api src/support/ticket-context.ts) before persisting.
 *
 * sessionId/traceId are the correlation keys: the same values are tagged on
 * every AppSignal error report from this session/page load, so triage can
 * match a ticket to its incidents.
 */

import { getOrCreateSessionId, getPageLoadTraceId } from '~/lib/session-id'
import { getRecentClientErrors } from '~/lib/client-error-buffer'

export interface TicketContextPayload {
  url?: string
  userAgent?: string
  viewport?: string
  appRevision?: string
  sessionId?: string
  traceId?: string
  recentClientErrors?: ReturnType<typeof getRecentClientErrors>
  collectedAt: string
}

// The revision doesn't change within a page load — fetch /healthz once and
// reuse. Same-origin, so no env/CORS concerns.
let cachedRevision: Promise<string | undefined> | undefined

function fetchAppRevision(): Promise<string | undefined> {
  cachedRevision ??= (async () => {
    try {
      const res = await fetch('/healthz', {
        signal: AbortSignal.timeout(2_000),
        credentials: 'same-origin',
      })
      if (!res.ok) return undefined
      const data = (await res.json()) as { version?: string }
      return typeof data.version === 'string' ? data.version : undefined
    } catch {
      return undefined
    }
  })()
  return cachedRevision
}

export async function collectTicketContext(): Promise<TicketContextPayload | undefined> {
  if (typeof window === 'undefined') return undefined
  const appRevision = await fetchAppRevision()
  const errors = getRecentClientErrors(10)
  return {
    url: window.location.href,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    appRevision,
    sessionId: getOrCreateSessionId(),
    traceId: getPageLoadTraceId(),
    recentClientErrors: errors.length > 0 ? errors : undefined,
    collectedAt: new Date().toISOString(),
  }
}
